import { booleanAttribute, ChangeDetectionStrategy, Component, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faLock, faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { ImageDirective } from '../../../../../../@common/DOM/image.directive';
import { ToDatePipe } from '../../../../../../@common/pipe/to-date.pipe';
import { TypeToStringPipe } from '../../../../../../@common/pipe/type-to-string.enum';
import { EventInfo, PublishedStatus } from '../../../../../../@generated';
import { AppRoute } from '../../../../../../routing/types/app-route';
import { BadgeComponent } from '../../../../global/badge/badge.component';

@Component({
    selector: 'app-event-item',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ RouterLink, ImageDirective, ToDatePipe, FaIconComponent, BadgeComponent, TypeToStringPipe ],
    styles: `
        @use "variables" as *;
        @use "functions" as *;

        a {
            text-decoration: none;
            display: block;
            width: 100%;
            background-color: #fff;
            border-radius: 0;
            color: inherit;
            transition: transform 150ms ease;
        }

        .card-item-header {
            position: relative;
            width: 256px;
            height: 59px;

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                }

            @media screen and (max-width: 768px) {
                width: 190px;
                height: 41px;
            }
            //Mobile
             @media screen and (max-width: 400px) {
                width: 165px;
                height: 36px;
            }
        }

        .private-badge {
            position: absolute;
            top: 10px;
            left: 10px;
            border-radius: 4px !important;
            color: white;
            z-index: 10;
            padding: 0.35rem 0.55rem;
            background: $red;
            font-size: 14px;

            @media screen and (max-width: 768px) {
                top: 8px;
                left: 8px;
                font-size: 10px;
                padding: 0.25rem 0.45rem;
            }
            //Mobile
             @media screen and (max-width: 400px) {
                top: 6px;
                left: 6px;
                font-size: 9px;
                padding: 0.2rem 0.35rem;
            }
        }

        .card-item-body {
            background-color: #fff;
            padding: 0.6rem 0 0 !important;
            max-width: 256px;

            @media screen and (max-width: 768px) {
                max-width: 190px;
            }
            //Mobile
             @media screen and (max-width: 400px) {
                max-width: 165px;
            }

            h3 {
                font-size: 16px;
                font-weight: 600;
                line-height: 20px;
                color: $dark-red;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                width: 100%;

                @media screen and (max-width: 768px) {font-size: 14px; line-height: 18px; }

            }


            .info {
                margin-top: 10px;
                .date-views {
                    display: flex;
                    align-items: center;
                    color: $dark-blue;
                    //Desktop
                    gap: 20px;
                    font-size: 13px;
                    line-height: 16px;
                    //Mobile
                    @media screen and (max-width: 768px) {
                        gap: 10px;
                        font-size: 10px;
                        line-height: 14px;
                    }
                    font-weight: 400;

                    .date-group,
                    .videos-group {
                        display: flex;
                        align-items: center;
                        gap: 4px;
                    }

                    .icon-wrapper {
                        display: inline-flex;
                        align-items: center;
                        
                        svg {
                            width: 18px;
                            height: 18px;
                            color: $dark-blue;
                        }

                        img {
                            width: 18px;
                            height: 18px;
                            filter: brightness(0) saturate(100%) invert(10%) sepia(37%) saturate(2654%) hue-rotate(208deg) brightness(95%) contrast(101%);
                        }
                    }
                }
            }
        }

        .event-list-view {
            display: flex;
            align-items: center;
            padding: 0.5rem;
            margin: 0.5rem 0;
            cursor: pointer;

            .event-title {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                margin-bottom: 0.25rem;
            }

            img {
                aspect-ratio: 16/9;
                height: 70px;
                margin-right: 1rem;
                object-fit: cover;
                transform: scale(1.05);

                @media screen and (max-width: 600px) {
                    height: 60px;
                    margin-right: 0.75rem;
                }

                @media screen and (max-width: 450px) {
                    height: 50px;
                    margin-right: 0.75rem;
                }
            }

            .right {
                display: flex;
                align-items: center;
                width: 100%;

                strong {
                    color: $dark;
                    width: 60%;
                    margin-right: 1.5rem;
                }

                div {
                    display: flex;
                    gap: 0.5rem;
                    width: 40%;

                    span {
                        width: 50%;
                        min-width: fit-content;
                    }
                }

                @media screen and (max-width: 767px) {
                    flex-direction: column;

                    strong {
                        width: 100%;
                        margin-right: 0;
                    }

                    div {
                        width: 100%;

                        span {
                            width: fit-content;
                            margin-right: 1rem;
                        }
                    }
                }
            }
        }
    `,
    template: `
        @if (!listView()) {
            <a class="card-item" [routerLink]="['/', AppRoutes.Events, event().slug]">
                <div class="card-item-header">
                    @if (event().status === PublishedStatus.Private) {
                        <div class="private-badge">
                            <fa-icon [icon]="faLock"/>
                            Private
                        </div>
                    }

                    @if (event().status === PublishedStatus.Draft) {
                        <div class="private-badge">
                            <fa-icon [icon]="faPencilAlt"/>
                            Draft
                        </div>
                    }

                    <img [src]="event().thumbnail" [alt]="event().title + ' image'"
                         [attr.loading]="priorityImageLoading() ? 'eager' : 'lazy'"
                         [attr.fetchpriority]="priorityImageLoading() ? 'high' : 'low'"
                         [attr.decoding]="'async'"
                    />
                </div>
                <div class="card-item-body">
                    <h3>{{ event().title }}</h3>
                    <div class="info">
                        <p class="date-views">
                            <span class="date-group">
                                <span class="icon-wrapper">
                                    <img src="/assets/icons/calendar.svg" alt="calendar icon" width="18" height="18" />
                                </span>
                                {{ event().date | toDate }}
                            </span>
                            <span class="videos-group">
                                <span class="icon-wrapper">
                                    <img src="/assets/icons/video.svg"  alt="videos icon" width="18" height="18"/>
                                </span>
                                {{ event().total_videos }} videos
                            </span>
                        </p>
                    </div>
                </div>
                <!-- <div class="card-item-footer">
                    @if (event().type) {
                        <app-badge [text]="event().type! | typeToString"/>
                    }
                </div> -->
            </a>
        } @else {
            <a class="event-list-view" [routerLink]="['/', AppRoutes.Events, event().slug]"> >
                <img [src]="event().thumbnail" alt="video thumbnail"
                     [attr.loading]="priorityImageLoading() ? 'eager' : 'lazy'"
                     [attr.fetchpriority]="priorityImageLoading() ? 'high' : 'low'"
                     [attr.decoding]="'async'"
                />
                <div class="right">
                    <strong class="event-title">{{ event().title }}</strong>
                    <div class="numbers">
                        <span class="light-color">{{ event().date | toDate }}</span>
                        <span class="light-color">{{ event().total_videos }} Videos</span>
                    </div>
                </div>
            </a>
        }
    `
})
export class EventItemComponent {
    public event = input.required<EventInfo>();
    public priorityImageLoading = input(false, { transform: booleanAttribute });
    public listView = input(false, { transform: booleanAttribute });
    public AppRoutes = AppRoute;
    protected readonly PublishedStatus = PublishedStatus;
    protected readonly faLock = faLock;
    protected readonly faPencilAlt = faPencilAlt;
}
